import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import IconToolTip from '../../common/IconToolTip';
import Loader from '../../common/Loader';
import { getAttachmentOcrDetails } from './pavAssetUtils';

// One badge per image, so it is always visible which photo produced which
// reading. The panel is portalled to the body instead of dispatched as a modal:
// the PAV screen itself can already be running inside one.
const OCR_STATES = {
    reading: { icon: 'document_scanner', badgeClass: 'bg-scogobgsky text-white', title: 'Scanning image for details...' },
    failed: { icon: 'warning_amber', badgeClass: 'bg-amber-600 text-white', title: 'Could not read image details — click to view' },
    empty: { icon: 'info_outline', badgeClass: 'bg-slate-500 text-white', title: 'No details detected from this image' },
    read: { icon: 'text_snippet', badgeClass: 'bg-emerald-600 text-white', title: 'View scanned details read from this image' },
};

// The badge rides on a thumbnail, so it follows the thumbnail's size: the
// roomier full-screen tiles get a target that is actually easy to hit.
const BADGE_SIZES = {
    sm: { button: 'h-5 gap-0.5 px-1.5', icon: 'text-font11', count: 'text-font10' },
    lg: { button: 'h-6 gap-0.5 px-1.5', icon: 'text-font14', count: 'text-font11' },
};

const formatConfidence = (confidence) => {
    const value = Number(confidence);
    if (!value || value < 0) return '';
    return `${Math.round(value <= 1 ? value * 100 : value)}%`;
};

const OcrFieldRow = ({ field, result, onUse }) => {
    const confidence = formatConfidence(field.confidence);
    return (
        <div className='min-w-0 rounded-md border border-scogoddd bg-scogof8 px-3 py-2'>
            <div className='flex items-center justify-between gap-2'>
                <span className='truncate text-font10 font-medium uppercase tracking-wide text-scogo99'>{field.label}</span>
                {confidence && <span className='shrink-0 rounded bg-white px-1 py-px text-font10 font-medium text-scogo99' title='OCR confidence'>{confidence}</span>}
            </div>
            <div className='mt-1 flex items-start justify-between gap-2'>
                <span className='min-w-0 break-words text-font12 font-medium text-scogo21'>{String(field.value)}</span>
                {onUse && (
                    <button
                        type='button'
                        onClick={onUse}
                        title={`Put this value into the asset's ${field.label} field`}
                        className={`flex shrink-0 items-center gap-1 rounded border px-2 py-1 text-font10 font-medium transition-colors ${result?.ok
                            ? 'border-scogoprimary bg-scogolightblue text-scogoprimary'
                            : 'border-scogoddd bg-white text-scogo21 hover:border-scogoprimary hover:text-scogoprimary'}`}
                    >
                        <span className='material-icons-outlined text-font12'>{result?.ok ? 'check' : 'input'}</span>
                        {result?.ok ? 'Used' : 'Use'}
                    </button>
                )}
            </div>
            {result && !result.ok && (
                <p className='mt-1 text-font10 font-normal leading-4 text-scogoorange'>This asset type has no matching field.</p>
            )}
        </div>
    );
};

const OcrDetailsPanel = ({ attachment, onClose, onUseValue }) => {
    const { state, fields, error } = getAttachmentOcrDetails(attachment);
    const [results, setResults] = useState({});
    const [summary, setSummary] = useState('');

    useEffect(() => {
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', closeOnEscape);
        return () => window.removeEventListener('keydown', closeOnEscape);
    }, [onClose]);

    // onUseValue returns the label of the field it filled, or null when this
    // asset type has nowhere to put the value.
    const applyValue = (field) => {
        const filledLabel = onUseValue(field);
        setResults((current) => ({ ...current, [field.key]: { ok: Boolean(filledLabel) } }));
        setSummary(filledLabel ? `${filledLabel} filled from this image.` : '');
    };

    const applyEveryValue = () => {
        const nextResults = {};
        let filledCount = 0;
        fields.forEach((field) => {
            const filledLabel = onUseValue(field);
            nextResults[field.key] = { ok: Boolean(filledLabel) };
            if (filledLabel) filledCount += 1;
        });
        setResults(nextResults);
        setSummary(filledCount > 0
            ? `${filledCount} field${filledCount === 1 ? '' : 's'} filled from this image.`
            : 'None of these values match a field on this asset type.');
    };

    return createPortal(
        <div className='fixed inset-0 z-[70] flex items-center justify-center p-4' role='dialog' aria-modal='true' aria-label='Details read from this image'>
            <div className='fixed inset-0 bg-black opacity-25' onClick={onClose} />
            <div className='relative flex max-h-[80vh] w-full max-w-[42rem] flex-col overflow-hidden rounded-lg border-0 bg-white shadow-lg outline-none'>
                <div className='flex shrink-0 items-center gap-3 border-b border-scogoddd px-5 py-3'>
                    <img src={attachment.preview_url || attachment.url} alt={attachment.name} className='h-12 w-12 shrink-0 rounded-md border border-scogoddd object-cover' />
                    <div className='min-w-0 flex-1'>
                        <p className='truncate text-font16 font-medium text-scogo15'>Details read from this image</p>
                        <p className='truncate text-font11 font-normal text-scogo99' title={attachment.name}>{attachment.name}</p>
                    </div>
                    <div className='cursor-pointer' onClick={onClose}>
                        <IconToolTip title='Close'>
                            <span className='material-icons align-middle text-font24 font-medium text-scogoprimary hover:text-scogoorange'>highlight_off</span>
                        </IconToolTip>
                    </div>
                </div>

                <div className='min-h-0 flex-1 overflow-y-auto px-5 py-4'>
                    {state === 'reading' && (
                        <p className='flex items-center gap-2 text-font12 font-normal text-scogo21'>
                            <Loader size='14' thickness='2' color='#12355F' />
                            Reading this image...
                        </p>
                    )}
                    {state === 'failed' && (
                        <p className='rounded-md border border-scogoorange bg-scogoLightOrange px-3 py-2 text-font12 font-normal leading-5 text-scogo21'>
                            {error || 'This image could not be read. Enter the details on the asset card.'}
                        </p>
                    )}
                    {state === 'empty' && (
                        <p className='rounded-md border border-scogoddd bg-scogof8 px-3 py-2 text-font12 font-normal leading-5 text-scogo21'>
                            No asset details were read from this image. Enter them on the asset card.
                        </p>
                    )}
                    {state === 'read' && (
                        <>
                            {onUseValue && (
                                <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
                                    <p className='text-font11 font-normal text-scogo99'>{summary || 'Use a value to put it into the asset card.'}</p>
                                    <button type='button' onClick={applyEveryValue} className='flex shrink-0 items-center gap-1 rounded border border-scogoprimary bg-white px-2 py-1 text-font11 font-medium text-scogoprimary transition-colors hover:bg-scogolightblue'>
                                        <span className='material-icons-outlined text-font13'>done_all</span>
                                        Use all from this image
                                    </button>
                                </div>
                            )}
                            <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
                                {fields.map((field) => (
                                    <OcrFieldRow
                                        key={field.key}
                                        field={field}
                                        result={results[field.key]}
                                        onUse={onUseValue ? () => applyValue(field) : undefined}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <p className='shrink-0 border-t border-scogoddd bg-scogof8 px-5 py-2 text-font11 font-normal leading-5 text-scogo99'>
                    These readings only prefill the asset card. Every field stays editable, so correct anything the scan got wrong.
                </p>
            </div>
        </div>,
        document.body
    );
};

export default function PavOcrDetailsButton({ attachment, className = '', showCount = true, size = 'sm', onUseValue }) {
    const [isOpen, setIsOpen] = useState(false);
    const { state, fields } = getAttachmentOcrDetails(attachment);
    const badgeSize = BADGE_SIZES[size] || BADGE_SIZES.sm;
    const stateConfig = OCR_STATES[state] || OCR_STATES.empty;
    const { icon, badgeClass } = stateConfig;
    const tooltipTitle = state === 'read' && fields.length > 0
        ? `${fields.length} detail${fields.length === 1 ? '' : 's'} read from this image (Click to view)`
        : stateConfig.title;

    return (
        <>
            <button
                type='button'
                title={tooltipTitle}
                aria-label={tooltipTitle}
                onClick={(event) => {
                    event.stopPropagation();
                    setIsOpen(true);
                }}
                className={`z-20 flex items-center rounded-full shadow-sm ring-1 ring-white/80 transition-all hover:scale-105 hover:shadow ${badgeSize.button} ${badgeClass} ${className}`}
            >
                <span className={`material-icons leading-none ${badgeSize.icon}`}>{icon}</span>
                {showCount && fields.length > 0 && <span className={`font-bold leading-none ${badgeSize.count}`}>{fields.length}</span>}
            </button>
            {isOpen && <OcrDetailsPanel attachment={attachment} onClose={() => setIsOpen(false)} onUseValue={onUseValue} />}
        </>
    );
}
